import { useState } from 'react';
import { mockAnalysisSession, mockProducts } from '@/data/mockData';
import { RegimenStep } from '@/types';

export function Regimen() {
  const [activeTab, setActiveTab] = useState<'am' | 'pm' | 'weekly'>('am');
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({});
  const [checkInData, setCheckInData] = useState({
    adherence: 5,
    symptoms: [] as string[],
    notes: '',
    skinCondition: 'improved'
  });

  const regimen = mockAnalysisSession.result?.regimen;

  if (!regimen) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" style={{ background: '#F7F5EE' }}>
        <div className="text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-semibold mb-2" style={{ color: '#1A2E12' }}>No Regimen Found</h2>
          <p className="mb-4" style={{ color: '#6B7B6E' }}>Complete a skin analysis to get your personalized regimen</p>
          <button
            className="px-6 py-3 rounded-lg font-medium text-white"
            style={{ background: 'linear-gradient(135deg,#BDD630,#567D38)' }}
          >
            Start Analysis
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = (step: RegimenStep) => {
    if (step.productId) {
      alert(`Added ${step.productName} to cart!`);
    }
  };

  const handleCompleteStep = (stepId: string) => {
    setCompletedSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const handleCheckIn = () => {
    setShowCheckIn(true);
  };

  const submitCheckIn = () => {
    console.log('Check-in submitted:', checkInData);
    setShowCheckIn(false);
  };

  const currentSteps =
    activeTab === 'am' ? regimen.amRoutine : activeTab === 'pm' ? regimen.pmRoutine : regimen.weeklyTreatments;

  const totalSteps =
    (regimen.amRoutine?.length || 0) + (regimen.pmRoutine?.length || 0) + (regimen.weeklyTreatments?.length || 0);
  const totalCompleted = Object.values(completedSteps).filter(Boolean).length;
  const adherencePct = totalSteps > 0 ? Math.round((totalCompleted / totalSteps) * 100) : 78;
  const currentWeek = 3;

  const tabs: { id: 'am' | 'pm' | 'weekly'; label: string }[] = [
    { id: 'am', label: 'AM' },
    { id: 'pm', label: 'PM' },
    { id: 'weekly', label: 'Weekly' },
  ];

  return (
    <div style={{ background: '#F7F5EE', minHeight: '70vh', padding: '40px 28px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <main className="lg:col-span-3">
            {/* Eyebrow / Heading / Adherence card / Check-in button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 16,
                marginBottom: 24,
              }}
            >
              <div>
                <div
                  style={{
                    textTransform: 'uppercase',
                    letterSpacing: '.1em',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#E07A5F',
                  }}
                >
                  Your Regimen
                </div>
                <h1 style={{ fontSize: 38, color: '#567D38', margin: '4px 0 0', fontWeight: 700 }}>
                  {regimen.name}
                </h1>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <button
                  onClick={handleCheckIn}
                  className="px-6 py-3 rounded-lg font-medium text-white"
                  style={{ background: 'linear-gradient(135deg,#BDD630,#567D38)' }}
                >
                  Daily Check-in
                </button>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 16,
                    padding: '14px 20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    minWidth: 220,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 13,
                      color: '#6B7B6E',
                      marginBottom: 6,
                    }}
                  >
                    <span>Week {currentWeek} of {regimen.durationWeeks}</span>
                    <span style={{ color: '#1A2E12', fontWeight: 600 }}>{adherencePct}% adherence</span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 999,
                      background: '#ede9de',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${adherencePct}%`,
                        background: 'linear-gradient(135deg,#BDD630,#567D38)',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Routine Tabs */}
            <div
              style={{
                display: 'flex',
                gap: 28,
                borderBottom: '2px solid #ede9de',
                marginBottom: 20,
              }}
            >
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    padding: '10px 2px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === t.id ? '2px solid #BDD630' : '2px solid transparent',
                    marginBottom: -2,
                    fontWeight: 600,
                    fontSize: 15,
                    color: activeTab === t.id ? '#1A2E12' : '#6B7B6E',
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Step list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {currentSteps.map((step, index) => {
                const done = !!completedSteps[step.id];
                return (
                  <div
                    key={step.id}
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: '16px 20px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      opacity: done ? 0.6 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'rgba(189,214,48,.15)',
                        color: '#567D38',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 15,
                          color: '#1A2E12',
                          textDecoration: done ? 'line-through' : 'none',
                        }}
                      >
                        {step.productName || step.instruction}
                      </div>
                      <div style={{ fontSize: 13, color: '#6B7B6E' }}>{step.instruction}</div>
                      <div style={{ fontSize: 12, color: '#E07A5F', marginTop: 2 }}>{step.frequency}</div>
                      {step.duration && (
                        <div style={{ fontSize: 12, color: '#6B7B6E' }}>Duration: {step.duration}</div>
                      )}
                    </div>
                    {step.productId && (
                      <button
                        onClick={() => handleAddToCart(step)}
                        className="px-3 py-1 rounded text-xs font-medium text-white flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg,#BDD630,#567D38)' }}
                      >
                        Add to Cart
                      </button>
                    )}
                    <button
                      onClick={() => handleCompleteStep(step.id)}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 6,
                        flexShrink: 0,
                        cursor: 'pointer',
                        background: done ? '#567D38' : '#fff',
                        border: '2px solid ' + (done ? '#567D38' : '#D9B38C'),
                        color: '#fff',
                        fontSize: 13,
                        lineHeight: 1,
                      }}
                    >
                      {done && '✓'}
                    </button>
                  </div>
                );
              })}
            </div>

            {regimen.notes && (
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: 'rgba(189,214,48,.15)',
                  border: '1px solid #BDD630',
                  borderRadius: 12,
                }}
              >
                <h4 style={{ fontWeight: 600, color: '#1A2E12', marginBottom: 8 }}>Notes</h4>
                <p style={{ fontSize: 14, color: '#1A2E12' }}>{regimen.notes}</p>
              </div>
            )}

            {/* Progress Tracking */}
            <div
              style={{
                marginTop: 24,
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                padding: 24,
              }}
            >
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1A2E12', marginBottom: 16 }}>Progress Tracking</h3>
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📊</div>
                <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1A2E12', marginBottom: 8 }}>
                  Track Your Progress
                </h4>
                <p style={{ color: '#6B7B6E', marginBottom: 16 }}>
                  Complete daily check-ins to track improvements in your skin condition
                </p>
                <button
                  className="px-6 py-3 rounded-lg font-medium text-white"
                  style={{ background: 'linear-gradient(135deg,#BDD630,#567D38)' }}
                >
                  View Progress Charts
                </button>
              </div>
            </div>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="space-y-6">
              {/* Regimen Info */}
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 24 }}>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: '#1A2E12', marginBottom: 16 }}>Regimen Info</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6B7B6E' }}>Duration</span>
                    <span style={{ color: '#1A2E12', fontWeight: 600 }}>{regimen.durationWeeks} weeks</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6B7B6E' }}>Start Date</span>
                    <span style={{ color: '#1A2E12', fontWeight: 600 }}>Jan 15, 2024</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6B7B6E' }}>Next Check-in</span>
                    <span style={{ color: '#1A2E12', fontWeight: 600 }}>Tomorrow</span>
                  </div>
                </div>
              </div>

              {/* Recommended Products */}
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 24 }}>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: '#1A2E12', marginBottom: 16 }}>Your Products</h3>
                <div className="space-y-3">
                  {mockAnalysisSession.result?.suggestedProducts.slice(0, 3).map((productId, index) => {
                    const product = mockProducts.find((p) => p.id === productId);
                    return product ? (
                      <div key={product.id} className="flex items-center space-x-3">
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: 'rgba(189,214,48,.15)',
                            color: '#567D38',
                          }}
                          className="flex items-center justify-center text-xs font-semibold"
                        >
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2E12' }}>{product.name}</p>
                          <p style={{ fontSize: 12, color: '#6B7B6E' }}>{product.routineStep}</p>
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: '#1A2E12' }}>R {product.price}</p>
                      </div>
                    ) : null;
                  })}
                </div>
                <button
                  className="w-full mt-4 px-4 py-2 rounded-lg font-medium text-white"
                  style={{ background: 'linear-gradient(135deg,#BDD630,#567D38)' }}
                >
                  Add All to Cart
                </button>
              </div>

              {/* Quick Actions */}
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 24 }}>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: '#1A2E12', marginBottom: 16 }}>Quick Actions</h3>
                <div className="space-y-2">
                  {['📸 Upload Progress Photo', '📝 Add Notes', '📊 View Full Progress', '🔄 Request Re-analysis'].map(
                    (label) => (
                      <button
                        key={label}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm"
                        style={{ color: '#1A2E12' }}
                        onMouseOver={(e) => (e.currentTarget.style.background = '#F7F5EE')}
                        onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Daily Check-in Modal */}
      {showCheckIn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="max-w-md w-full mx-4 p-6" style={{ background: '#fff', borderRadius: 16 }}>
            <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1A2E12', marginBottom: 16 }}>Daily Check-in</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A2E12' }}>
                  How well did you follow your routine today? (1-10)
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={checkInData.adherence}
                  onChange={(e) => setCheckInData({ ...checkInData, adherence: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-center text-sm mt-1" style={{ color: '#6B7B6E' }}>
                  {checkInData.adherence}/10
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A2E12' }}>
                  Any symptoms or concerns?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Redness', 'Dryness', 'Breakouts', 'Irritation', 'Oiliness', 'No issues'].map((symptom) => (
                    <label key={symptom} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={checkInData.symptoms.includes(symptom)}
                        onChange={(e) => {
                          const symptoms = checkInData.symptoms;
                          if (e.target.checked) {
                            setCheckInData({ ...checkInData, symptoms: [...symptoms, symptom] });
                          } else {
                            setCheckInData({ ...checkInData, symptoms: symptoms.filter((s) => s !== symptom) });
                          }
                        }}
                        style={{ accentColor: '#567D38' }}
                      />
                      <span className="text-sm" style={{ color: '#1A2E12' }}>
                        {symptom}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A2E12' }}>
                  How does your skin feel today?
                </label>
                <select
                  value={checkInData.skinCondition}
                  onChange={(e) => setCheckInData({ ...checkInData, skinCondition: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg"
                  style={{ border: '1px solid #D9B38C', color: '#1A2E12' }}
                >
                  <option value="much_worse">Much worse than yesterday</option>
                  <option value="worse">Worse than yesterday</option>
                  <option value="same">About the same</option>
                  <option value="improved">Improved</option>
                  <option value="much_improved">Much improved</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A2E12' }}>
                  Notes (optional)
                </label>
                <textarea
                  value={checkInData.notes}
                  onChange={(e) => setCheckInData({ ...checkInData, notes: e.target.value })}
                  placeholder="Any observations or notes about your skin today..."
                  className="w-full px-3 py-2 rounded-lg"
                  style={{ border: '1px solid #D9B38C', color: '#1A2E12' }}
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCheckIn(false)}
                className="px-4 py-2 rounded-lg"
                style={{ border: '1px solid #D9B38C', color: '#1A2E12' }}
              >
                Cancel
              </button>
              <button
                onClick={submitCheckIn}
                className="px-4 py-2 rounded-lg font-medium text-white"
                style={{ background: 'linear-gradient(135deg,#BDD630,#567D38)' }}
              >
                Submit Check-in
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
